/**
 * TEMPORARY COMPONENT - DELETE AFTER USER MIGRATION COMPLETE
 * Purpose: Splash page for migrating Teams users from Azure AD to Entra External ID
 * Created: 2025-10-15
 * Expected sunset: After all users migrated from Azure AD synthetic emails to Entra ID personal emails
 * Related issues: #750, #751, #752
 * URL shown in Teams app manifest during migration period
 * Route: /user-migration-teams
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import npsfLogoWhite from '../assets/npsf-logo-white.png';
import '../styles/UserMigrationTeamsSplash.css';

const UserMigrationTeamsSplash: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userEmail = searchParams.get('email') || '';
  const userId = searchParams.get('userId') || '';

  const handleMigrateAccount = () => {
    // Open account migration page in external browser (last action in Teams)
    const params = new URLSearchParams();
    if (userEmail) params.append('email', userEmail);
    if (userId) params.append('userId', userId);

    const migrationUrl = `https://athena4partners.org/migrate-account${params.toString() ? '?' + params.toString() : ''}`;

    window.open(migrationUrl, '_blank');
  };

  return (
    <div className="migration-splash-root">
      <Box className="migration-content-container">
        <Stack spacing={4} alignItems="center" sx={{ maxWidth: '700px', margin: '0 auto' }}>
          {/* Logo */}
          <img
            src={npsfLogoWhite}
            alt="NPSF Logo"
            style={{ width: '150px', height: 'auto' }}
          />

          {/* Main Header */}
          <Typography variant="h3" component="h1" className="migration-header">
            Welcome to the New Athena Experience!
          </Typography>

          {/* Main Message */}
          <Typography variant="h6" className="migration-message">
            We're excited to bring you an improved Athena!
          </Typography>
          <Typography variant="h6" className="migration-message">
            Sign in and enjoy secure access directly from your preferred browser.
          </Typography>

          {/* Current Account Display */}
          {userEmail && (
            <Alert severity="info" sx={{ width: '100%' }}>
              <Typography variant="body1">
                <strong>Current Teams account:</strong> {userEmail}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Update to a long term email address of your choice
              </Typography>
            </Alert>
          )}

          {/* Primary CTA Button */}
          <Button
            variant="contained"
            size="large"
            startIcon={<AccountCircleIcon />}
            onClick={handleMigrateAccount}
            className="migration-button"
            sx={{
              backgroundColor: '#ffd700',
              color: '#002855',
              padding: '1rem 3rem',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#ffed4e',
              },
            }}
          >
            Move to the new athena NOW!
          </Button>

          {/* Support Section */}
          <Box className="migration-support-section">
            <Typography variant="body1" className="migration-support-text">
              Need help? Contact{' '}
              <a href="mailto:athena@npsfoundation.org" className="support-email-link">
                athena@npsfoundation.org
              </a>
            </Typography>
          </Box>
        </Stack>
      </Box>
    </div>
  );
};

export default UserMigrationTeamsSplash;
